import { ReactNode, useState, MouseEvent as ReactMouseEvent, Fragment } from 'react';
import PreviewCard from 'src/components/ui/PreviewCard';
import SanitizedMarkdown from 'src/components/ui/SanitizedMarkdown';
import Button from 'src/components/ui/Button';
import {
  ArrowSquareOutIcon,
  BookIcon,
  BuildingsIcon,
  CalendarBlankIcon,
  CalendarCheckIcon,
  CaretDownIcon,
  CaretUpIcon,
  ClockCountdownIcon,
  DotIcon,
  FileTextIcon,
  FlagIcon,
  GraduationCapIcon,
  LinkSimpleIcon,
  ListChecksIcon,
  MapPinIcon,
  MinusIcon,
  PaperclipIcon,
  RepeatIcon,
  ScrollIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UsersThreeIcon
} from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { RESOURCE_ICON, resolveResourceHref, resourcePath } from './ResourceLink';
import {
  prefetchResourcePreview,
  useResourcePreview,
  type ActionPreviewMetadata,
  type ActionPreviewUpdate,
  type AssetReviewPreviewMetadata,
  type ChatPreviewMetadata,
  type DashboardPreviewMetadata,
  type DocumentPreviewMetadata,
  type GenericPreviewMetadata,
  type MeetingPreviewMetadata,
  type ObligationPreviewMetadata,
  type MeetingSeriesPreviewMetadata,
  type PamSubmissionPreviewMetadata,
  type PamSubmissionQuestionPreviewMetadata,
  type ResourcePreview,
  type ResourcePreviewMetadata,
  type ReviewPreviewMetadata,
  type SpecialistPreviewMetadata,
  type AppointmentEvidencePreview,
  type TeamGroupPreviewMetadata,
  type TrainingAppointmentPreviewMetadata,
  type WritingPreviewMetadata
} from '../hooks/useResourcePreview';
import { dashboardAssuranceColors, type AssuranceLevel } from 'src/features/reporting/utils/color';
import { mapLevelToAssurance } from 'src/features/reporting/utils/apiTransforms';
import { AssuranceGauge } from 'src/features/reporting/components/AssuranceGauge';
import type { ChartSpec } from 'src/components/charts';
import { AssuranceLevelPill, TileChart } from 'src/features/reporting/components/MetricTileCharts';
import { formatPeriodLabel } from 'src/features/reporting/utils/constants';
import type { Level } from 'src/features/reporting/api/types';
import { DRAFT_DOCUMENT_TYPE, friendlySource } from 'src/features/document-hub/types';
import { EVIDENCE_STATUS } from 'src/features/training-hub/trainingStatus';
import { useChartTilesEnabled } from 'src/features/reporting/hooks/useDashboardTileStyle';
import { GRADE_COLORS, GRADE_ORDER } from 'src/features/pfi/components/conditionGrade';

interface ResourcePopoverProps {
  /** Resource ULID — used as the cache key and the fetch path segment. */
  resourceId: string;
  /** Navigation target. Matched to what clicking the ``LinkChip`` does so
   * the "Open" button stays predictable (same tenant prefix, same tab
   * semantics). */
  href: string;
  /** Human label for the "Open" CTA — e.g. "Open document",
   * "Open action". Kept resource-type aware so the CTA reads naturally. */
  openLabel: string;
  /** Wrapped ``LinkChip`` (or similar) that acts as the hover trigger. */
  children: ReactNode;
  /** Called when the popover opens — lets the parent capture analytics
   * (chat_resource_link_hovered, etc.). */
  onOpen?: () => void;
  /** Click handler forwarded to the "Open" button. Mirrors the
   * ``LinkChip``'s ``onClick`` so navigation analytics fire from either
   * entry point. */
  onOpenClick?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}

/** Heading shown for a citation whose resource is gone or no longer visible. */
export const RESOURCE_UNAVAILABLE_LABEL = 'Resource unavailable';

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 150;

/**
 * Hover-driven preview card for an inline ``<resource>`` citation.
 *
 * Shell logic (hover timing, "Open" footer) lives here. The middle section
 * is dispatched through
 * {@link ResourceMetadataRenderer} into a per-type renderer — each
 * resource kind has bespoke layout requirements, and a single
 * label/value table didn't flex well enough (e.g. meetings want a
 * date+time row with an icon; actions want an activity feed).
 */
export function ResourcePopover({ resourceId, href, openLabel, children, onOpen, onOpenClick }: ResourcePopoverProps) {
  const [hasHovered, setHasHovered] = useState(false);
  const state = useResourcePreview(resourceId, hasHovered);

  const markHovered = () => {
    setHasHovered(true);
    prefetchResourcePreview(resourceId);
  };

  return (
    <PreviewCard
      delay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      onTriggerHover={markHovered}
      onOpen={() => {
        markHovered();
        onOpen?.();
      }}
      className="p-3 w-[340px] max-w-[min(340px,90vw)] overflow-visible flex flex-col"
      content={<ResourcePopoverBody state={state} href={href} openLabel={openLabel} onOpenClick={onOpenClick} />}
    >
      {children}
    </PreviewCard>
  );
}

interface ResourcePopoverBodyProps {
  state: ReturnType<typeof useResourcePreview>;
  href: string;
  openLabel: string;
  onOpenClick?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}

function ResourcePopoverBody({ state, href, openLabel, onOpenClick }: ResourcePopoverBodyProps) {
  const location = useLocation();
  if (state.status === 'loading') {
    return (
      <div className="flex flex-col">
        <div className="h-4 bg-gray-100 rounded animate-pulse mb-2 w-3/4" />
        <div className="h-3 bg-gray-100 rounded animate-pulse mb-1 w-1/2" />
        <div className="h-3 bg-gray-100 rounded animate-pulse mb-1 w-1/3" />
        <OpenResourceFooter href={href} openLabel={openLabel} onOpenClick={onOpenClick} />
      </div>
    );
  }
  if (state.status === 'not_found') {
    return (
      <div className="flex flex-col">
        <div className="text-sm text-content-default font-medium">{RESOURCE_UNAVAILABLE_LABEL}</div>
        <div className="text-xs text-content-subtlest mt-1">
          This resource has been deleted or you no longer have access to it.
        </div>
      </div>
    );
  }
  if (state.status === 'error' || !state.data) {
    return (
      <div className="flex flex-col">
        <div className="text-sm text-content-default font-medium">Unable to load preview</div>
        <OpenResourceFooter href={href} openLabel={openLabel} onOpenClick={onOpenClick} />
      </div>
    );
  }

  // When the middle body renders nothing (e.g. writing, generic, or a
  // sparse resource with no summary/details) the header's bottom
  // divider sits directly on top of the footer's top divider and
  // reads as a double rule. Drop the header rule in that case so a
  // single separator sits between the title and the "Open" button.
  const hasBody = hasBodyContent(state.data.metadata);
  const resolvedHref = resolveResourceHref(state.data.metadata, href, location.pathname);
  const resolvedLabel = resolveOpenLabel(state.data.metadata, openLabel);
  return (
    <div className="flex flex-col text-content-subtle">
      <PopoverHeader preview={state.data} showDivider={hasBody} />
      <ResourceMetadataRenderer metadata={state.data.metadata} />
      <OpenResourceFooter href={resolvedHref} openLabel={resolvedLabel} onOpenClick={onOpenClick} />
    </div>
  );
}

/** Mirror of :func:`resolveResourceHref` for the "Open" button's label.
 * Training Hub previews carry an ``open_label`` (e.g. "Open in Water
 * Safety Group", "View in your Profile") that reflects WHERE the
 * click is actually taking the user — much more informative than the
 * default "Open appointment". */
function resolveOpenLabel(metadata: ResourcePreviewMetadata, defaultLabel: string): string {
  if (metadata.kind === 'training_appointment' && metadata.open_label) {
    return metadata.open_label;
  }
  return defaultLabel;
}

// --- shared shell building blocks --------------------------------------
//
// The header (title + subtitle row) and footer ("Open" button) are the
// same across every resource type; only the body between them varies.

function PopoverHeader({ preview, showDivider }: { preview: ResourcePreview; showDivider: boolean }) {
  const subtitleParts = headerSubtitle(preview);
  const meta = preview.metadata;
  const displayTitle = meta.kind === 'action' ? splitActionDescription(preview.title).title : preview.title;
  const showExtractedTitle =
    meta.kind === 'document' && !meta.is_wiki && !!meta.extracted_title && meta.extracted_title !== preview.title;
  return (
    <div className={`pb-1.5 flex-shrink-0${showDivider ? ' border-b border-border-subtle' : ''}`}>
      <div className="font-medium text-sm text-content-default break-words">{displayTitle}</div>
      {showExtractedTitle && (
        <div className="text-xs text-content-subtlest truncate mt-0.5">{meta.extracted_title}</div>
      )}
      {subtitleParts.length > 0 && <SubtitleRow parts={subtitleParts} />}
    </div>
  );
}

/** Small pill used in subtitles for short categorical values (e.g.
 * the document jurisdiction). Matches the look of the old ``FileLink``
 * popover where "UK" sat in a rounded background tile next to the
 * authority label. */
function SubtitleBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md bg-background-neutral text-content-subtle px-1.5 py-[1px] text-[11px] font-medium">
      {children}
    </span>
  );
}

/** Extracts the short header subtitle from a preview. Returns a
 * mixed list of strings and ReactNodes so individual segments can
 * render as rich elements (badges, icons) without forcing every
 * renderer to special-case them. Kept centralised so every resource
 * type leads with the same visual shape instead of ad-hoc variants.
 */
function headerSubtitle(preview: ResourcePreview): ReactNode[] {
  const meta = preview.metadata;
  // Every resource type leads with the same shape: a small icon tile
  // naming the resource kind (e.g. "Document", "Action"). Gives the
  // popover subtitle a consistent anchor regardless of how much
  // type-specific context follows.
  const parts: ReactNode[] = [<TypeSegment key="type" preview={preview} />];
  switch (meta.kind) {
    case 'action':
      if (meta.reference) parts.push(meta.reference);
      if (meta.status) parts.push(meta.status);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'obligation':
      if (meta.party) parts.push(meta.party);
      if (meta.obligation_kind) parts.push(meta.obligation_kind);
      if (meta.lifecycle) parts.push(meta.lifecycle);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'document': {
      // Wiki documents mirror the old ``FileLink`` popover exactly
      // (year · authority · UK). User-uploaded documents instead
      // surface who added them and when — the two audiences want
      // different provenance: "is this source trustworthy?" for the
      // former, "who uploaded this and when?" for the latter. The
      // leading ``TypeSegment`` already names the document kind, so
      // we don't repeat ``document_type`` here.
      if (meta.is_wiki) {
        const year = yearFromDate(meta.publication_date);
        if (year) parts.push(year);
        if (meta.authority) parts.push(meta.authority);
        if (meta.jurisdiction) parts.push(meta.jurisdiction);
      } else {
        const isEditorDocument = meta.source_type === DRAFT_DOCUMENT_TYPE;
        if (!isEditorDocument) {
          const source = friendlySource(meta.source_type);
          if (source) parts.push(source);
        }
        const provenance = isEditorDocument ? 'Created' : 'Uploaded';
        if (meta.created_at) {
          const createdBy = meta.created_by_name
            ? `${provenance} ${meta.created_at} by ${meta.created_by_name}`
            : `${provenance} ${meta.created_at}`;
          parts.push(createdBy);
        } else if (meta.created_by_name) {
          parts.push(`${provenance} by ${meta.created_by_name}`);
        }
      }
      break;
    }
    case 'meeting':
      if (meta.status) parts.push(meta.status);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'meeting_series':
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'team_group':
      if (meta.short_reference) parts.push(meta.short_reference);
      break;
    case 'review':
      // ``review_type`` (e.g. "Annual policy review") is more specific
      // than the generic "Review" label in the leading segment, so we
      // keep it as the next chip when present.
      if (meta.review_type) parts.push(meta.review_type);
      if (meta.status) parts.push(meta.status);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'writing':
      // Show the template name (e.g. "Meeting minutes") as the
      // most identifying signal — tells the reader what kind of
      // writing they're looking at before "last updated" context.
      if (meta.writing_type) parts.push(meta.writing_type);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'chat':
      if (meta.started_at) parts.push(`Started ${meta.started_at}`);
      break;
    case 'specialist':
      if (meta.creator_name) parts.push(`by ${meta.creator_name}`);
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'dashboard':
      // Period is the most identifying secondary signal — tells the
      // viewer whether the preview is showing the current month or
      // a stale snapshot. Prefer the same cadence-aware formatter
      // the dashboard tiles use so "Q1 FY26" / "2026-W37" match
      // the page the hover is previewing.
      if (meta.period_year != null && (meta.period_month != null || meta.period_week != null)) {
        parts.push(formatPeriodLabel(meta.period_year, meta.period_month ?? 1, meta.update_cadence, meta.period_week));
      } else if (meta.period_label) {
        parts.push(meta.period_label);
      } else {
        pushUpdatedAt(parts, meta.modified_at);
      }
      break;
    case 'asset_review':
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'training_appointment':
      // Status is the most identifying secondary signal — "expired"
      // or "revoked" should be visible in the subtitle so a reader
      // doesn't click into the body to discover it.
      parts.push(humaniseStatus(meta.status));
      pushUpdatedAt(parts, meta.modified_at);
      break;
    case 'pam_submission_question':
      parts.push(meta.question_ref);
      parts.push(meta.final_answer ? meta.final_answer : 'Unanswered');
      break;
    case 'pam_submission':
      parts.push(meta.status);
      parts.push(String(meta.year));
      break;
    case 'generic':
      pushUpdatedAt(parts, meta.modified_at);
      break;
  }
  return parts;
}

/** Capitalise the first letter of a status string for display
 * ("active" → "Active"). Kept inline because the strings we're
 * humanising are an extremely small, known set; pulling in a real
 * casing library would be overkill. */
function humaniseStatus(status: string): string {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Leading subtitle chip: a resource-type icon next to the human-
 * readable type label (e.g. [🗓️ Meeting], [📄 Document]). Kept in
 * sync with the inline ``ResourceLink`` chip's ``RESOURCE_ICON``
 * registry so a citation and its popover header show the same icon.
 */
function TypeSegment({ preview }: { preview: ResourcePreview }) {
  const { icon: Icon, label } = typeSegmentFor(preview);
  return (
    <span className="inline-flex items-center gap-1 text-content-subtle">
      <Icon size={12} className="flex-shrink-0" />
      <span>{label}</span>
    </span>
  );
}

function typeSegmentFor(preview: ResourcePreview): {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
} {
  const meta = preview.metadata;
  // Documents split two ways: the Regulatory Wiki gets its own
  // "BookIcon" icon + label (matching the nav entry) so wiki citations
  // read differently from freeform user uploads that share the same
  // backend ``document`` type.
  if (meta.kind === 'document') {
    if (meta.is_wiki) return { icon: BookIcon, label: 'Wiki' };
    if (meta.source_type === DRAFT_DOCUMENT_TYPE) return { icon: FileTextIcon, label: 'Editor Document' };
    return { icon: FileTextIcon, label: 'Document' };
  }
  const icon = RESOURCE_ICON[preview.resource_type] ?? LinkSimpleIcon;
  if (meta.kind === 'generic') return { icon, label: meta.type_label };
  return { icon, label: TYPE_LABELS[meta.kind] };
}

// Human-facing labels for each discriminated-union ``kind``. Kept
// in lockstep with the backend ``_TYPE_LABELS`` map in
// ``resources/service.py`` so the popover, the chat citation, and
// the rendered XML all speak the same vocabulary.
const TYPE_LABELS: Record<Exclude<ResourcePreviewMetadata['kind'], 'generic' | 'document'>, string> = {
  action: 'Action',
  obligation: 'Obligation',
  meeting: 'Meeting',
  meeting_series: 'Meeting series',
  team_group: 'Group',
  review: 'Review',
  writing: 'Writing',
  chat: 'Chat',
  specialist: 'Specialist',
  asset_review: 'Asset review',
  dashboard: 'Dashboard',
  training_appointment: 'Appointment',
  pam_submission: 'PAM submission',
  pam_submission_question: 'PAM question'
};

function pushUpdatedAt(parts: ReactNode[], modifiedAt: string | null | undefined) {
  if (modifiedAt) parts.push(`Updated ${modifiedAt}`);
}

/** Pull the four-digit year out of the backend's ``D MMM YYYY`` date
 * format (or any string that ends in a year). Returns ``undefined``
 * if no year can be recovered — callers then skip the segment. */
function yearFromDate(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const match = value.match(/(\d{4})/);
  return match ? match[1] : undefined;
}

function SubtitleRow({ parts }: { parts: ReactNode[] }) {
  return (
    <div className="text-xs flex items-center flex-wrap gap-y-0.5 mt-0.5">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && <DotIcon size={14} weight="light" className="text-content-subtle" />}
          {typeof part === 'string' ? <span>{part}</span> : part}
        </Fragment>
      ))}
    </div>
  );
}

interface OpenResourceFooterProps {
  href: string;
  openLabel: string;
  onOpenClick?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}

function OpenResourceFooter({ href, openLabel, onOpenClick }: OpenResourceFooterProps) {
  return (
    <div className="flex justify-end pt-2 mt-2 border-t border-border-subtle">
      <Button
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="outline"
        size="sm"
        text={openLabel}
        icon={ArrowSquareOutIcon}
        iconPosition="right"
        onClick={(event) => onOpenClick?.(event as ReactMouseEvent<HTMLAnchorElement>)}
      />
    </div>
  );
}

// --- per-type renderer dispatch ----------------------------------------

/** Mirror of each renderer's "would I render anything?" check. Kept
 * in lockstep with the early-return conditions in the per-type body
 * components so the outer layout knows whether to draw the divider
 * between header and body. Not exhaustive by accident — each branch
 * tracks the exact signals the matching renderer actually uses. */
function hasBodyContent(meta: ResourcePreviewMetadata): boolean {
  switch (meta.kind) {
    case 'action':
      return true;
    case 'obligation':
      return true;
    case 'document': {
      const sharedGroups = meta.shared_with_groups ?? [];
      const hasGroups = !meta.is_wiki && sharedGroups.length > 0;
      return !!meta.summary || hasGroups;
    }
    case 'meeting': {
      const schedule = formatSchedule(meta.scheduled_date, meta.scheduled_time);
      const groupLine = meta.group_title || meta.series_title;
      return !!schedule || !!meta.location || !!groupLine || !!meta.summary;
    }
    case 'meeting_series':
      return !!meta.group_title || typeof meta.meeting_count === 'number' || !!meta.next_meeting_date;
    case 'team_group': {
      const hasViewerSignals =
        typeof meta.member_count === 'number' ||
        !!meta.viewer_role ||
        (meta.viewer_open_action_count ?? 0) > 0 ||
        !!meta.last_meeting_date;
      return hasViewerSignals || !!meta.description;
    }
    case 'review':
      return !!meta.document_title || typeof meta.compliance_score === 'number' || !!meta.started_at || !!meta.summary;
    case 'chat':
      return !!meta.first_user_message || !!meta.first_assistant_message;
    case 'specialist':
      return !!meta.instruction_excerpt || !!meta.response_style;
    case 'asset_review':
      return meta.total_observations > 0 || meta.uploads.length > 0;
    case 'dashboard': {
      const hasTrail = (meta.historical_assurance_levels ?? []).some((level) => level !== null && level !== undefined);
      return !!meta.assurance_level || hasTrail;
    }
    case 'training_appointment':
      // Always render the body — the appointee + domain + role + dates
      // are the appointment's identifying signals; without them the
      // popover would be effectively empty.
      return true;
    case 'pam_submission_question':
      return true;
    case 'pam_submission':
      return true;
    case 'writing':
    case 'generic':
      return false;
  }
}

function ResourceMetadataRenderer({ metadata }: { metadata: ResourcePreviewMetadata }) {
  switch (metadata.kind) {
    case 'action':
      return <ActionPreviewBody meta={metadata} />;
    case 'obligation':
      return <ObligationPreviewBody meta={metadata} />;
    case 'document':
      return <DocumentPreviewBody meta={metadata} />;
    case 'meeting':
      return <MeetingPreviewBody meta={metadata} />;
    case 'meeting_series':
      return <MeetingSeriesPreviewBody meta={metadata} />;
    case 'team_group':
      return <TeamGroupPreviewBody meta={metadata} />;
    case 'review':
      return <ReviewPreviewBody meta={metadata} />;
    case 'writing':
      return <WritingPreviewBody meta={metadata} />;
    case 'chat':
      return <ChatPreviewBody meta={metadata} />;
    case 'specialist':
      return <SpecialistPreviewBody meta={metadata} />;
    case 'asset_review':
      return <AssetReviewPreviewBody meta={metadata} />;
    case 'dashboard':
      return <DashboardPreviewBody meta={metadata} />;
    case 'training_appointment':
      return <TrainingAppointmentPreviewBody meta={metadata} />;
    case 'pam_submission_question':
      return <PamSubmissionQuestionPreviewBody meta={metadata} />;
    case 'pam_submission':
      return <PamSubmissionPreviewBody meta={metadata} />;
    case 'generic':
      return <GenericPreviewBody meta={metadata} />;
  }
}

// --- generic building blocks used by the per-type bodies ---------------

/** Wrapper around the middle "body" section. Returns null when empty
 * so the footer sits flush against the header on sparse types. */
function Body({ children }: { children: ReactNode }) {
  return <div className="mt-2 flex flex-col gap-2">{children}</div>;
}

/** Single row in the icon-led "details" list used by most types.
 * Icons keep each line visually scannable — users can pick out a
 * date, a location, or an assignee list without reading labels. */
function DetailRow({
  icon: Icon,
  children,
  title
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: ReactNode;
  title?: string;
}) {
  return (
    <div className="flex items-center gap-1 text-xs" title={title}>
      <Icon size={14} className="mt-0.5 flex-shrink-0" />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

function DescriptionBlock({ text }: { text: string }) {
  return <div className="text-xs  whitespace-pre-wrap break-words line-clamp-4 italic">{text}</div>;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-[10px] font-semibold  uppercase tracking-wide">{children}</div>;
}

// --- action --------------------------------------------------------------

const ACTION_BODY_CHAR_LIMIT = 280;

function splitActionDescription(text: string): { title: string; body: string } {
  const normalised = text.replace(/\r\n/g, '\n').trim();
  const newline = normalised.search(/\n+/);
  if (newline === -1) return { title: normalised, body: '' };
  return {
    title: normalised.slice(0, newline).trim(),
    body: normalised.slice(newline).trim()
  };
}

function cappedText(text: string, limit: number): string {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}…`;
}

function ActionPreviewBody({ meta }: { meta: ActionPreviewMetadata }) {
  const hasUpdates = meta.updates.length > 0;
  const body = splitActionDescription(meta.description ?? '').body;
  const bodyPreview = body ? cappedText(body, ACTION_BODY_CHAR_LIMIT) : '';
  return (
    <Body>
      {bodyPreview && <div className="text-xs text-content-default whitespace-pre-wrap break-words">{bodyPreview}</div>}
      <div className="flex flex-col gap-1">
        <DetailRow icon={UsersThreeIcon}>
          {meta.assignees.length > 0 ? (
            meta.assignees.join(', ')
          ) : (
            <span className="italic text-content-subtlest">Unassigned</span>
          )}
        </DetailRow>
        <DetailRow icon={ClockCountdownIcon}>
          {meta.due_date ? `Due ${meta.due_date}` : <span className="italic text-content-subtlest">No due date</span>}
        </DetailRow>
        <DetailRow icon={FlagIcon}>
          {meta.priority ? (
            `${meta.priority.charAt(0).toUpperCase()}${meta.priority.slice(1)} priority`
          ) : (
            <span className="italic text-content-subtlest">No priority</span>
          )}
        </DetailRow>
      </div>
      {hasUpdates && <UpdatesFeed updates={meta.updates} />}
    </Body>
  );
}

function ObligationPreviewBody({ meta }: { meta: ObligationPreviewMetadata }) {
  return (
    <Body>
      <div className="flex flex-col gap-1">
        <DetailRow icon={meta.obligation_kind === 'Recurring' ? RepeatIcon : CalendarBlankIcon}>
          {meta.schedule}
        </DetailRow>
        <DetailRow icon={ScrollIcon}>{meta.health}</DetailRow>
        {meta.clause_ref && <DetailRow icon={FileTextIcon}>{meta.clause_ref}</DetailRow>}
        {meta.group_name && <DetailRow icon={UsersThreeIcon}>{meta.group_name}</DetailRow>}
      </div>
    </Body>
  );
}

function UpdatesFeed({ updates }: { updates: ActionPreviewUpdate[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <SectionLabel>Latest updates</SectionLabel>
      <ul className="flex flex-col gap-2">
        {updates.map((update, index) => (
          <li key={`${index}-${update.timestamp ?? ''}`} className="text-xs">
            <div className="text-content-subtlest flex items-center">
              {update.timestamp && <span>{update.timestamp}</span>}
              {update.timestamp && update.author && (
                <DotIcon size={14} weight="light" className="text-content-subtlest" />
              )}
              {update.author && <span>{update.author}</span>}
            </div>
            <div className="text-content-default whitespace-pre-wrap break-words line-clamp-3">{update.text}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- document ------------------------------------------------------------

function DocumentPreviewBody({ meta }: { meta: DocumentPreviewMetadata }) {
  const sharedGroups = meta.shared_with_groups ?? [];
  const hasGroups = !meta.is_wiki && sharedGroups.length > 0;
  if (!meta.summary && !hasGroups) return null;
  return (
    <Body>
      {meta.summary && <MarkdownSummary text={meta.summary} />}
      {hasGroups && (
        <DetailRow icon={UsersThreeIcon} title={sharedGroups.join(', ')}>
          <SharedGroupsList groups={sharedGroups} />
        </DetailRow>
      )}
    </Body>
  );
}

/** Renders the shared-groups list with each group name styled as a
 * subtle badge. Capped at 3 with a "+N more" overflow so a document
 * shared broadly doesn't blow up the popover height; the full list
 * is still available via the row's ``title`` tooltip. */
function SharedGroupsList({ groups }: { groups: string[] }) {
  const MAX_VISIBLE = 3;
  const visible = groups.slice(0, MAX_VISIBLE);
  const overflow = groups.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-content-subtle">Shared with</span>
      {visible.map((name) => (
        <SubtitleBadge key={name}>{name}</SubtitleBadge>
      ))}
      {overflow > 0 && <span className="text-content-subtle">+{overflow} more</span>}
    </div>
  );
}

const SUMMARY_CHAR_LIMIT = 300;

/** Markdown-rendered summary for document-ish bodies. Truncates long
 * summaries to keep the popover compact. */
function MarkdownSummary({ text }: { text: string }) {
  const displayed = text.length > SUMMARY_CHAR_LIMIT ? text.slice(0, SUMMARY_CHAR_LIMIT).trimEnd() + '…' : text;
  return (
    <div className="prose prose-sm !max-w-none text-xs">
      <SanitizedMarkdown>{displayed}</SanitizedMarkdown>
    </div>
  );
}

// --- meeting -------------------------------------------------------------

function MeetingPreviewBody({ meta }: { meta: MeetingPreviewMetadata }) {
  // Compose "DATE at TIME" inline so the popover reads like a calendar
  // entry rather than a two-row date/time split. Falls back gracefully
  // if only one of the two is set.
  const schedule = formatSchedule(meta.scheduled_date, meta.scheduled_time);
  const groupLine = meta.group_title
    ? meta.series_title
      ? `${meta.series_title} · ${meta.group_title}`
      : meta.group_title
    : (meta.series_title ?? null);
  const hasAny = schedule || meta.location || groupLine || meta.summary;
  if (!hasAny) return null;

  return (
    <Body>
      <div className="flex flex-col gap-1">
        {schedule && <DetailRow icon={CalendarBlankIcon}>{schedule}</DetailRow>}
        {meta.location && <DetailRow icon={MapPinIcon}>{meta.location}</DetailRow>}
        {groupLine && <DetailRow icon={UsersThreeIcon}>{groupLine}</DetailRow>}
      </div>
      {meta.summary && <DescriptionBlock text={meta.summary} />}
    </Body>
  );
}

function formatSchedule(date: string | null | undefined, time: string | null | undefined): string | null {
  if (date && time) return `${date} at ${time}`;
  return date ?? time ?? null;
}

// --- meeting series ------------------------------------------------------

function MeetingSeriesPreviewBody({ meta }: { meta: MeetingSeriesPreviewMetadata }) {
  const rows: ReactNode[] = [];
  if (meta.group_title) {
    rows.push(
      <DetailRow key="group" icon={UsersThreeIcon}>
        {meta.group_title}
      </DetailRow>
    );
  }
  if (meta.meeting_count !== null && meta.meeting_count !== undefined) {
    rows.push(
      <DetailRow key="count" icon={RepeatIcon}>
        {meta.meeting_count} {meta.meeting_count === 1 ? 'meeting' : 'meetings'}
      </DetailRow>
    );
  }
  if (meta.next_meeting_date) {
    rows.push(
      <DetailRow key="next" icon={CalendarCheckIcon}>
        Next: {meta.next_meeting_date}
      </DetailRow>
    );
  }
  if (rows.length === 0) return null;
  return (
    <Body>
      <div className="flex flex-col gap-1">{rows}</div>
    </Body>
  );
}

// --- team group ----------------------------------------------------------

function TeamGroupPreviewBody({ meta }: { meta: TeamGroupPreviewMetadata }) {
  const rows: ReactNode[] = [];
  if (typeof meta.member_count === 'number') {
    rows.push(
      <DetailRow key="members" icon={UsersThreeIcon}>
        {formatMemberCount(meta.member_count)}
      </DetailRow>
    );
  }
  if (meta.viewer_role) {
    rows.push(
      <DetailRow key="viewer-role" icon={UserCircleIcon}>
        You are {articleFor(meta.viewer_role)} {meta.viewer_role}
      </DetailRow>
    );
  }
  const openActions = meta.viewer_open_action_count ?? 0;
  if (openActions > 0) {
    rows.push(
      <DetailRow key="open-actions" icon={ListChecksIcon}>
        You have {openActions} open {openActions === 1 ? 'action' : 'actions'} in this group
      </DetailRow>
    );
  }
  if (meta.last_meeting_date) {
    rows.push(
      <DetailRow key="last-meeting" icon={CalendarBlankIcon}>
        Last meeting{' '}
        {meta.last_meeting_id ? (
          <MeetingDateLink meetingId={meta.last_meeting_id} label={meta.last_meeting_date} />
        ) : (
          meta.last_meeting_date
        )}
      </DetailRow>
    );
  }
  if (rows.length === 0) {
    // Fall back to the original description preview when none of the
    // richer per-viewer signals are available — usually means a group
    // the viewer isn't a member of. Keeps the popover useful instead
    // of collapsing to just the header.
    if (!meta.description) return null;
    return (
      <Body>
        <DescriptionBlock text={meta.description} />
      </Body>
    );
  }
  return <Body>{rows}</Body>;
}

function formatMemberCount(count: number): string {
  return count === 1 ? '1 member' : `${count} members`;
}

/** Renders the "last meeting" date as a navigable link into the
 * meeting page. Uses the same tenant-prefix logic as
 * :component:`ResourceLink` so it behaves like the citation chips
 * — opens in a new tab, respects the user's current tenant scope.
 */
function MeetingDateLink({ meetingId, label }: { meetingId: string; label: string }) {
  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);
  const rawPath = resourcePath('meeting', meetingId);
  const href = tenantCode ? toTenantPath(rawPath, tenantCode) : rawPath;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
    >
      {label}
    </a>
  );
}

/** English "a/an" article for a role label. Covers the three labels
 * we emit server-side (Admin / Member / Viewer) — kept inline rather
 * than pulling in a dependency for one use. */
function articleFor(word: string): 'a' | 'an' {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

// --- review --------------------------------------------------------------

function ReviewPreviewBody({ meta }: { meta: ReviewPreviewMetadata }) {
  const rows: ReactNode[] = [];
  if (meta.document_title) {
    rows.push(
      <DetailRow key="doc" icon={FileTextIcon}>
        {meta.document_title}
      </DetailRow>
    );
  }
  if (meta.compliance_score !== null && meta.compliance_score !== undefined) {
    rows.push(
      <DetailRow key="score" icon={ShieldCheckIcon}>
        Score: {Math.round(meta.compliance_score * 100)}%
      </DetailRow>
    );
  }
  if (meta.started_at) {
    rows.push(
      <DetailRow key="started" icon={CalendarBlankIcon}>
        Started {meta.started_at}
      </DetailRow>
    );
  }
  const hasAny = rows.length > 0 || !!meta.summary;
  if (!hasAny) return null;
  return (
    <Body>
      {rows.length > 0 && <div className="flex flex-col gap-1">{rows}</div>}
      {meta.summary && <DescriptionBlock text={meta.summary} />}
    </Body>
  );
}

// --- writing -------------------------------------------------------------

function WritingPreviewBody({ meta: _meta }: { meta: WritingPreviewMetadata }) {
  // Writings surface everything useful via the header (title + type
  // + updated-at) — the body stays empty so the popover sits flush
  // against the footer rather than showing a bare divider.
  return null;
}

// --- chat ----------------------------------------------------------------

function ChatPreviewBody({ meta }: { meta: ChatPreviewMetadata }) {
  // Chats preview as "opening exchange" — the first user turn plus
  // the start of the assistant's reply. Gives a hover reader the
  // same cue they'd get by re-opening the conversation, without
  // pulling rich parts (files, tool calls, citations) into the
  // popover. Both fields are optional server-side: a chat with
  // only a user turn still renders cleanly.
  const hasUser = !!meta.first_user_message;
  const hasAssistant = !!meta.first_assistant_message;
  if (!hasUser && !hasAssistant) return null;
  return (
    <Body>
      {hasUser && <ChatTurn role="user" text={meta.first_user_message as string} />}
      {hasAssistant && <ChatTurn role="assistant" text={meta.first_assistant_message as string} />}
    </Body>
  );
}

/** Compact transcript row used by the chat popover. Styled as a
 * mini chat bubble — user turns hug the right edge, assistant turns
 * hug the left — so the popover reads like a trimmed conversation
 * rather than a two-field form. Body text is clamped rather than
 * truncated server-side in full; the backend already caps length,
 * this prevents unusually wide paragraphs from stretching the
 * popover. */
function ChatTurn({ role, text }: { role: 'user' | 'assistant'; text: string }) {
  const isUser = role === 'user';
  const label = isUser ? 'You' : 'Assistant';
  return (
    <div className={`flex flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-content-subtle">{label}</div>
      <div
        className={`text-xs text-content-default whitespace-pre-wrap break-words line-clamp-6 rounded-lg px-2 py-1 max-w-[85%] ${
          isUser ? 'bg-bluegray-100' : 'bg-primary-lightblue'
        }`}
      >
        {text}
      </div>
    </div>
  );
}

// --- asset review --------------------------------------------------------

function AssetReviewPreviewBody({ meta }: { meta: AssetReviewPreviewMetadata }) {
  if (meta.total_observations === 0 && meta.uploads.length === 0) return null;

  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);

  const gradeEntries = Object.entries(meta.grade_counts).sort(
    ([a], [b]) => GRADE_ORDER.indexOf(a) - GRADE_ORDER.indexOf(b)
  );

  return (
    <Body>
      {meta.total_observations > 0 && (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Observations</SectionLabel>
          <div className="text-xs text-content-default">
            {meta.reviewed_observations} of {meta.total_observations} reviewed
          </div>
          {gradeEntries.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {gradeEntries.map(([grade, count]) => (
                <span
                  key={grade}
                  className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${GRADE_COLORS[grade] ?? 'bg-gray-100 text-gray-700'}`}
                >
                  {grade}: {count}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {meta.uploads.length > 0 && (
        <div className="flex flex-col gap-1">
          <SectionLabel>Condition reports</SectionLabel>
          <ul className="flex list-disc flex-col gap-0.5 pl-4">
            {meta.uploads.map((upload, i) => {
              if (upload.document_id) {
                const rawPath = resourcePath('document', upload.document_id);
                const href = tenantCode ? toTenantPath(rawPath, tenantCode) : rawPath;
                return (
                  <li key={i}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-misc-link hover:underline truncate block max-w-full"
                    >
                      {upload.filename}
                    </a>
                  </li>
                );
              }
              return (
                <li key={i} className="text-xs text-content-subtle truncate">
                  {upload.filename}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Body>
  );
}

// --- specialist ----------------------------------------------------------

function SpecialistPreviewBody({ meta }: { meta: SpecialistPreviewMetadata }) {
  if (!meta.instruction_excerpt && !meta.response_style) return null;
  return (
    <Body>
      {meta.instruction_excerpt && (
        <div className="flex flex-col gap-1">
          <SectionLabel>Role</SectionLabel>
          <div className="text-xs text-content-default whitespace-pre-wrap break-words line-clamp-4">
            {meta.instruction_excerpt}
          </div>
        </div>
      )}
      {meta.response_style && (
        <div className="flex flex-col gap-1">
          <SectionLabel>Response style</SectionLabel>
          <div className="text-xs text-content-subtle italic whitespace-pre-wrap break-words line-clamp-2">
            {meta.response_style}
          </div>
        </div>
      )}
    </Body>
  );
}

// --- dashboard -----------------------------------------------------------

/** Numeric ordering of assurance levels — used to decide whether the
 * current period is an improvement, regression, or no-op vs the prior
 * period. Mirrors the mapping used by the full reporting tile
 * (``LEVEL_TO_NUMERIC`` in ``MetricTile.tsx``) so the popover reads
 * the same trend direction as the main page. ``NaN`` maps to 0 so
 * "no data" slots are treated as missing rather than a fake low. */
const ASSURANCE_ORDER: Record<AssuranceLevel, number> = {
  NaN: 0,
  None: 1,
  Limited: 2,
  Moderate: 3,
  Substantial: 4,
  High: 5
};

type Trend = 'up' | 'down' | 'stable';

function assuranceFrom(level: string | null | undefined): AssuranceLevel {
  // Backend emits the raw ``Level`` enum string ("high" / "limited"
  // / ...); reuse the reporting mapper so the popover and the main
  // dashboard stay in lockstep when labels or cases change.
  return mapLevelToAssurance((level ?? null) as Level | null);
}

function dashboardTrend(current: AssuranceLevel, previous: AssuranceLevel): Trend | null {
  if (current === 'NaN' || previous === 'NaN') return null;
  if (ASSURANCE_ORDER[current] > ASSURANCE_ORDER[previous]) return 'up';
  if (ASSURANCE_ORDER[current] < ASSURANCE_ORDER[previous]) return 'down';
  return 'stable';
}

function DashboardPreviewBody({ meta }: { meta: DashboardPreviewMetadata }) {
  // Same viewer preference as MetricTile: chart tiles (default) or classic dial.
  const useChartTiles = useChartTilesEnabled();
  const current = assuranceFrom(meta.assurance_level);
  const previous = assuranceFrom(meta.previous_assurance_level);
  const trailLevels: AssuranceLevel[] = (meta.historical_assurance_levels ?? []).map((level) => assuranceFrom(level));
  const hasTrail = trailLevels.some((level) => level !== 'NaN');
  const hasCurrent = current !== 'NaN';

  if (!hasCurrent && !hasTrail) return null;

  const trend = dashboardTrend(current, previous);
  const cadence = meta.update_cadence ?? 'monthly';
  const periodWord = cadence === 'quarterly' ? 'quarter' : cadence === 'weekly' ? 'week' : 'month';

  if (useChartTiles) {
    return <DashboardChartPreview current={current} trend={trend} meta={meta} periodWord={periodWord} />;
  }

  return (
    <DashboardClassicPreview
      current={current}
      previous={previous}
      trend={trend}
      trailLevels={trailLevels}
      hasTrail={hasTrail}
      periodWord={periodWord}
      spec={meta.gauge_spec ?? null}
    />
  );
}

function DashboardChartPreview({
  current,
  trend,
  meta,
  periodWord
}: {
  current: AssuranceLevel;
  trend: Trend | null;
  meta: DashboardPreviewMetadata;
  periodWord: string;
}) {
  return (
    <Body>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          {current === 'NaN' ? (
            <span className="text-xs text-content-subtle italic">No data</span>
          ) : (
            <AssuranceLevelPill level={current} />
          )}
          {current !== 'NaN' && (
            <>
              <div className="w-px h-4 bg-gray-200 flex-shrink-0" aria-hidden />
              <div className="flex items-center gap-1.5 min-w-0">
                {trend ? (
                  <>
                    <TrendCaret trend={trend} />
                    <span className="text-xs font-semibold text-content-default">
                      {trend === 'up' ? 'Improved' : trend === 'down' ? 'Declined' : 'Stable'}
                    </span>
                    <span className="text-[11px] text-content-subtle whitespace-nowrap">vs last {periodWord}</span>
                  </>
                ) : (
                  <span className="text-xs text-content-subtle italic">No prior data</span>
                )}
              </div>
            </>
          )}
        </div>
        {meta.chart_spec ? (
          <TileChart spec={meta.chart_spec} height={96} ariaLabel="Assurance level history" />
        ) : (
          <div className="text-[10px] text-content-subtle italic">No history</div>
        )}
      </div>
    </Body>
  );
}

function DashboardClassicPreview({
  current,
  previous,
  trend,
  trailLevels,
  hasTrail,
  periodWord,
  spec
}: {
  current: AssuranceLevel;
  previous: AssuranceLevel;
  trend: Trend | null;
  trailLevels: AssuranceLevel[];
  hasTrail: boolean;
  periodWord: string;
  spec: ChartSpec | null;
}) {
  const colors = dashboardAssuranceColors(false);
  const trailHistoryCount = Math.max(0, trailLevels.length - 1);

  return (
    <Body>
      <div className="grid grid-cols-3 gap-2 items-stretch">
        <div className="flex flex-col justify-between items-start gap-1 min-w-0">
          {trend ? (
            <div>
              <div className="flex items-center gap-1">
                <TrendCaret trend={trend} />
                <span className="text-xs font-medium text-content-default">
                  {trend === 'up' ? 'Improved' : trend === 'down' ? 'Declined' : 'Stable'}
                </span>
              </div>
              {previous !== 'NaN' && <div className="mt-0.5 text-[11px] text-content-subtle">from {previous}</div>}
            </div>
          ) : (
            <div className="text-xs text-content-subtle italic">No prior data</div>
          )}
          <div className="text-[10px] text-content-subtle">vs last {periodWord}</div>
        </div>

        <div className="flex flex-col justify-between items-center gap-1">
          {spec && <AssuranceGauge spec={spec} level={current} size={96} />}
          <div className="text-[10px] text-content-subtle text-center">Aggregated Level</div>
        </div>

        <div className="flex flex-col justify-between items-end gap-1">
          {hasTrail ? (
            <AssuranceTrail levels={trailLevels} colors={colors} />
          ) : (
            <div className="text-[10px] text-content-subtle italic">No history</div>
          )}
          <div className="text-[10px] text-content-subtle">
            {trailHistoryCount > 0 ? `${trailHistoryCount} ${periodWord}s` : ''}
          </div>
        </div>
      </div>
    </Body>
  );
}

function TrendCaret({ trend }: { trend: Trend }) {
  if (trend === 'up') return <CaretUpIcon size={14} weight="bold" color="#16a34a" />;
  if (trend === 'down') return <CaretDownIcon size={14} weight="bold" color="#dc2626" />;
  return <MinusIcon size={14} weight="bold" color="#64748b" />;
}

/** Compact heatmap strip of the last N period levels (oldest →
 * newest, current included last). Classic-tile counterpart of the
 * reporting ``AssuranceLevelTrend`` — same colours and ordering,
 * just sized to fit the popover body. Missing periods render as a
 * muted grey block so gaps in the trail stay visible. */
function AssuranceTrail({ levels, colors }: { levels: AssuranceLevel[]; colors: Record<AssuranceLevel, string> }) {
  return (
    <div className="flex items-center gap-px h-8 w-full">
      {levels.map((level, index) => {
        const color = level === 'NaN' ? '#e5e7eb' : colors[level];
        return (
          <div
            key={index}
            className="flex-1 h-full rounded-[2px]"
            style={{ backgroundColor: color, minWidth: 6 }}
            title={level === 'NaN' ? 'No data' : level}
          />
        );
      })}
    </div>
  );
}

// --- training hub appointment -------------------------------------------

function TrainingAppointmentPreviewBody({ meta }: { meta: TrainingAppointmentPreviewMetadata }) {
  // Appointee + domain/role line + dates + scope (site or trust-wide)
  // + evidence. Mirrors the row layout on the Training Hub
  // Appointments tab so a chat hover reads the same way as the page
  // the user is about to navigate to.
  const dateLine = formatAppointedRange(meta.appointed_from, meta.appointed_until);
  const evidence = meta.evidence_documents;
  return (
    <Body>
      <div className="flex flex-col gap-1">
        <DetailRow icon={UserCircleIcon}>
          {meta.appointee_name}
          {meta.appointee_email && <span className="text-content-subtlest"> · {meta.appointee_email}</span>}
        </DetailRow>
        <DetailRow icon={GraduationCapIcon}>
          {meta.role_name}
          <span className="text-content-subtlest"> · {meta.domain_name}</span>
        </DetailRow>
        {dateLine && <DetailRow icon={CalendarBlankIcon}>{dateLine}</DetailRow>}
        <DetailRow icon={BuildingsIcon}>
          {meta.site_name ?? <span className="italic text-content-subtlest">Trust-wide</span>}
        </DetailRow>
        <div
          className="flex items-start gap-1 text-xs"
          title={evidence.length === 0 ? 'No evidence documents attached' : undefined}
        >
          <PaperclipIcon size={14} className="mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex flex-col gap-0.5">
            {evidence.length === 0 ? (
              <span className="italic text-content-subtlest">No evidence</span>
            ) : (
              evidence.map((item, index) => (
                <div key={index} className="break-words">
                  {item.title || 'Untitled document'}
                  <span className="text-content-subtlest">
                    {' '}
                    · {EVIDENCE_STATUS[item.status].label}, {formatEvidenceDates(item)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Body>
  );
}

/** Issue and expiry in one line. Dates are already formatted by the backend. */
function formatEvidenceDates(item: AppointmentEvidencePreview): string {
  const issued = item.issued_on ? `Issued ${item.issued_on}` : 'No issue date';
  const expires = item.expires_on ? `expires ${item.expires_on}` : 'never expires';
  return `${issued} · ${expires}`;
}

/** Format an appointment's date range into a single readable line.
 *
 * The dates arrive pre-formatted from the backend (UK day-first, with
 * Today / Yesterday / Tomorrow collapsing). We only need to combine
 * them, and handle the open-ended case where ``appointed_until`` is
 * absent. */
function formatAppointedRange(from: string | null | undefined, until: string | null | undefined): string | null {
  if (!from && !until) return null;
  if (from && until) return `${from} – ${until}`;
  if (from) return `From ${from} (open-ended)`;
  return `Until ${until}`;
}

// --- PAM submission question ----------------------------------------------

function PamSubmissionQuestionPreviewBody({ meta }: { meta: PamSubmissionQuestionPreviewMetadata }) {
  const rows: ReactNode[] = [];

  if (meta.question_text) {
    rows.push(<DescriptionBlock key="text" text={meta.question_text} />);
  }

  rows.push(
    <DetailRow key="saq" icon={ListChecksIcon}>
      {meta.saq_acronym} · {meta.saq_name} — {meta.saq_status}
    </DetailRow>
  );

  if (meta.final_answer) {
    rows.push(
      <DetailRow key="answer" icon={ShieldCheckIcon}>
        Answer: <strong>{meta.final_answer}</strong>
      </DetailRow>
    );
  }

  if (meta.assignees.length > 0) {
    rows.push(
      <DetailRow key="assignees" icon={UserCircleIcon}>
        {meta.assignees.join(', ')}
      </DetailRow>
    );
  }

  return <Body>{rows}</Body>;
}

// --- PAM submission -------------------------------------------------------

function PamSubmissionPreviewBody({ meta }: { meta: PamSubmissionPreviewMetadata }) {
  const rows: ReactNode[] = [];

  if (meta.questions_total > 0) {
    const pct = Math.round((meta.questions_answered / meta.questions_total) * 100);
    rows.push(
      <DetailRow key="questions" icon={ListChecksIcon}>
        {meta.questions_answered} of {meta.questions_total} questions answered ({pct}%)
      </DetailRow>
    );
  }

  if (meta.saqs_total > 0) {
    rows.push(
      <DetailRow key="saqs" icon={CalendarCheckIcon}>
        {meta.saqs_approved} of {meta.saqs_total} SAQs approved
      </DetailRow>
    );
  }

  if (meta.next_deadline) {
    rows.push(
      <DetailRow key="deadline" icon={ClockCountdownIcon}>
        Next deadline {meta.next_deadline}
      </DetailRow>
    );
  }

  if (meta.owner_name) {
    rows.push(
      <DetailRow key="owner" icon={UserCircleIcon}>
        Owner: {meta.owner_name}
      </DetailRow>
    );
  }

  if (meta.submitted_at) {
    rows.push(
      <DetailRow key="submitted" icon={CalendarBlankIcon}>
        Submitted {meta.submitted_at}
      </DetailRow>
    );
  }

  if (meta.approved_at) {
    rows.push(
      <DetailRow key="approved" icon={CalendarBlankIcon}>
        Approved {meta.approved_at}
      </DetailRow>
    );
  }

  if (rows.length === 0) return null;
  return <Body>{rows}</Body>;
}

// --- generic fallback ----------------------------------------------------

function GenericPreviewBody({ meta: _meta }: { meta: GenericPreviewMetadata }) {
  // Header already has everything the generic branch knows — leave
  // the body empty so we don't render a bare divider.
  return null;
}
